import React, { useState, useCallback, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { ViewSelector } from './ViewSelector';
import DailyCalendar from './DailyCalendar';
import { WeeklyCalendar } from './WeeklyCalendar';
import { MonthlyCalendar } from './MonthlyCalendar';
import { CurrentBookings } from './CurrentBookings';
import { RecurringEvents } from './RecurringEvents';
import { QuickBooking } from './QuickBooking';
import { format, startOfWeek, startOfMonth, isSameMonth, parseISO, isSameDay } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useBookings } from '@/contexts/BookingContext';
import type { BookingMetadata } from '@/lib/metadata-utils';
import type { Room, Booking, BookingStatus, StaffMember } from '@/types/booking';
import { staffConfig } from '@/config/staff';
import { useDevice } from '@/hooks/use-device';

export type { Room, Booking, BookingStatus, StaffMember };

export const staffMembers = staffConfig;

export type CalendarView = 'daily' | 'weekly' | 'monthly' | 'quick' | 'current' | 'recurring';

export default function BookingCalendar() {
  const { isMobile, isTablet } = useDevice();
  const isCompactView = isMobile || isTablet;
  
  const rooms: Room[] = [
    { id: 1, name: 'Main Hall', capacity: 350, color: 'bg-orange-50 border-l-4 border-orange-500' },
    { id: 2, name: 'Meeting Room', capacity: 20, color: 'bg-emerald-50 border-l-4 border-emerald-500' },
    { id: 3, name: 'Lounge 1', capacity: 100, color: 'bg-blue-50 border-l-4 border-blue-500' },
    { id: 4, name: 'Lounge 2', capacity: 50, color: 'bg-purple-50 border-l-4 border-purple-500' },
    { id: 5, name: 'Multi-purpose Space 2', capacity: 15, color: 'bg-pink-50 border-l-4 border-pink-500' },
    { id: 6, name: 'Multi-Purpose Space 1', capacity: 30, color: 'bg-cyan-50 border-l-4 border-cyan-500' },
    { id: 7, name: 'Studio 1: Music', capacity: 8, color: 'bg-indigo-50 border-l-4 border-indigo-500' },
    { id: 8, name: 'Studio 2: Podcast', capacity: 8, color: 'bg-rose-50 border-l-4 border-rose-500' }
  ];

  const mainRoom = rooms[0];
  const [view, setView] = useState<CalendarView>('monthly');
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [previousView, setPreviousView] = useState<CalendarView>('monthly');
  const { toast } = useToast();
  const { bookings, addBooking, updateBooking, deleteBooking } = useBookings();
  
  useEffect(() => {
    if (view === 'weekly' && !selectedRoom) {
      setSelectedRoom(mainRoom);
    } else if (view === 'monthly') {
      setSelectedRoom(null);
    }
  }, [view, selectedRoom, mainRoom]);

  // Ensure mobile users always see monthly view for calendar
  useEffect(() => {
    if (isCompactView && (view === 'daily' || view === 'weekly')) {
      setView('monthly');
    }
  }, [isCompactView, view]);

  const handleBookingCreate = useCallback((booking: Booking) => {
    addBooking(booking);
    const room = rooms.find(r => r.id === booking.roomId);
    toast({
      title: "Booking Created",
      description: `${room?.name} booked for ${booking.clientName}`,
    });
  }, [rooms, toast, addBooking]);

  const handleBookingUpdate = useCallback((updatedBooking: Booking) => {
    updateBooking(updatedBooking);
    const room = rooms.find(r => r.id === updatedBooking.roomId);
    toast({
      title: "Booking Updated",
      description: `${room?.name} booking updated for ${updatedBooking.clientName}`,
    });
  }, [rooms, toast, updateBooking]);

  const handleBookingDelete = useCallback((bookingId: number) => {
    deleteBooking(bookingId);
    toast({
      title: "Booking Deleted",
      description: "The booking has been removed",
    });
  }, [toast, deleteBooking]);

  const handleRecurringBookingsCreate = useCallback((newBookings: Booking[]) => {
    newBookings.forEach(addBooking);
    setView('monthly');
    return true;
  }, [addBooking]);

  const handleQuickBookingComplete = useCallback((date: Date) => {
    setDate(date);
    setView(isCompactView ? 'monthly' : 'monthly');
    
    toast({
      title: "Booking Created",
      description: `Viewing schedule for ${format(date, 'MMMM d, yyyy')}`,
    });
  }, [toast, isCompactView]);

  const handleRecurringComplete = useCallback(() => {
    setView('monthly');
  }, []);

  const handleViewChange = (newView: CalendarView) => {
    const today = new Date();
    
    setPreviousView(view);
    
    // Don't switch to daily or weekly on mobile
    if (isCompactView && (newView === 'daily' || newView === 'weekly')) {
      newView = 'monthly';
    }
    
    switch (newView) {
      case 'weekly':
        if (!selectedRoom) {
          setSelectedRoom(mainRoom);
        }
        setDate(prev => startOfWeek(isSameMonth(prev, today) ? prev : today, { weekStartsOn: 1 }));
        break;
      case 'monthly':
        setSelectedRoom(null);
        setDate(prev => startOfMonth(isSameMonth(prev, today) ? prev : today));
        break;
      case 'daily':
        setDate(today);
        break;
      case 'quick':
      case 'current':
      case 'recurring':
        break;
    }
    
    setView(newView);
  };

  const handleDayClick = (selectedDate: Date) => {
    setDate(selectedDate);
    // Always stay in monthly view when clicking a day, regardless of device
    setView('monthly');
    
    toast({
      title: "Day Selected",
      description: `Viewing schedule for ${format(selectedDate, 'MMMM d, yyyy')}`,
      duration: 2000,
    });
  };

  useEffect(() => {
    if (['quick', 'current', 'recurring'].includes(view)) {
      return;
    }
    setPreviousView(view);
  }, [view]);

  // Set monthly view as default for all devices
  useEffect(() => {
    setView('monthly');
    setDate(new Date());
  }, []);

  return (
    <div className="space-y-4 max-w-[95vw] mx-auto">
      <Card className="w-full">
        <div className="flex h-[calc(100vh-12rem)] flex-col">
          <div className="flex-1 overflow-hidden">
            <ViewSelector
              view={view}
              onViewChange={handleViewChange}
              date={date}
              onDateChange={setDate}
              selectedRoom={selectedRoom}
              onRoomSelect={setSelectedRoom}
              rooms={rooms}
              previousView={previousView}
            />
            <div className="h-[calc(100%-4rem)] overflow-auto">
              {view === 'daily' && (
                <DailyCalendar
                  rooms={rooms}
                  bookings={bookings}
                  date={date}
                  onBookingCreate={handleBookingCreate}
                  onBookingUpdate={handleBookingUpdate}
                  onBookingDelete={handleBookingDelete}
                />
              )}
              {view === 'weekly' && selectedRoom && (
                <WeeklyCalendar
                  room={selectedRoom}
                  bookings={bookings.filter(b => b.roomId === selectedRoom?.id)}
                  date={date}
                  onBookingCreate={handleBookingCreate}
                  onBookingUpdate={handleBookingUpdate}
                  onBookingDelete={handleBookingDelete}
                  onDayClick={handleDayClick}
                />
              )}
              {view === 'current' && (
                <CurrentBookings
                  rooms={rooms}
                  bookings={bookings}
                  onBookingUpdate={handleBookingUpdate}
                  onBookingDelete={handleBookingDelete}
                />
              )}
              {view === 'monthly' && (
                <MonthlyCalendar
                  rooms={rooms}
                  bookings={bookings}
                  selectedRoom={selectedRoom}
                  date={date}
                  onDayClick={handleDayClick}
                  onMonthChange={(monthIndex) => setDate(new Date(date.getFullYear(), monthIndex, 1))}
                  onBookingUpdate={handleBookingUpdate}
                  onBookingDelete={handleBookingDelete}
                />
              )}
              {view === 'recurring' && (
                <RecurringEvents 
                  rooms={rooms}
                  onBookingsCreate={handleRecurringBookingsCreate}
                  onClose={() => setView('monthly')}
                  onComplete={() => setView('monthly')}
                />
              )}
              {view === 'quick' && (
                <QuickBooking
                  rooms={rooms}
                  staffMembers={staffMembers}
                  onBookingCreate={handleBookingCreate}
                  onClose={() => setView('monthly')}
                  onBookingComplete={handleQuickBookingComplete}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}